import { useTranslation } from 'react-i18next'

import { getQaSelector } from '../../../../../utils/common'
import Button from '../../../../Button'
import { Checkbox, CHECKBOX_SIZE, CHECKBOX_TYPE } from '../../../../Checkbox'
import { Counter, TABLE_VARIANT } from '../../../../Counter'
import { EntityStatus } from '../../../../Entity'
import { APPLICANT_STAGES } from '../../../../RentalPortalApplicant'
import { Skeleton } from '../../../../Skeleton'
import Text from '../../../../Text'
import { getRentalPortalBoardSelectionState } from '../../../RentalPortal.utils'
import {
  APPLICANT_BOARD_COLUMN_CAP,
  APPLICANT_BOARD_SKELETON_CARDS,
  QA_NAME_APPLICANT_BOARD_COLUMN_SELECT,
  QA_NAME_APPLICANT_BOARD_MORE,
  QA_NAME_APPLICANT_BOARD_SKELETON_CARD,
} from '../RentalPortalApplicantBoard.constants'
import { RentalPortalApplicantBoardCard } from '../RentalPortalApplicantBoardCard'

import * as styles from './RentalPortalApplicantBoardColumn.module.css'

function RentalPortalApplicantBoardColumn({
  getActions,
  getIsItemSelectable,
  hasHandoff,
  hasSelection,
  isListingScoped,
  isLoading,
  items,
  selectedIds,
  selectNotAllowedTooltip,
  stage,
  total,
  onCallAction,
  onClickCard,
  onSelectCard,
  onSelectColumn,
  onShowMore,
}) {
  const { t } = useTranslation()

  const visibleItems = items.slice(0, APPLICANT_BOARD_COLUMN_CAP)

  const count = total ?? items.length

  const hiddenCount = count - visibleItems.length

  const { isAllSelected, isPartlySelected, selectableCount, selectableItems } =
    getRentalPortalBoardSelectionState({
      getIsSelectable: getIsItemSelectable,
      items,
      selectedIds,
    })

  const columnLabel = t(APPLICANT_STAGES[stage].text)

  const selectAllLabel = t('RentalPortal.BoardColumnSelectAllKey', {
    column: columnLabel,
  })

  function handleClickMore() {
    onShowMore?.(stage)
  }

  function handleSelectColumn(isChecked) {
    onSelectColumn(selectableItems, isChecked)
  }

  return (
    <div className={styles.RentalPortalApplicantBoardColumn}>
      <div className={styles.header}>
        {hasSelection ? (
          <Checkbox
            checked={isAllSelected}
            disabled={isLoading || selectableCount === 0}
            label={selectAllLabel}
            labelClassName={styles.selectLabel}
            qaName={QA_NAME_APPLICANT_BOARD_COLUMN_SELECT}
            selected={isPartlySelected}
            size={CHECKBOX_SIZE.small}
            tooltip={
              selectableCount === 0
                ? t('RentalPortal.BoardColumnSelectNoneAllowedKey', {
                    column: columnLabel,
                  })
                : selectAllLabel
            }
            type={CHECKBOX_TYPE.table}
            onChange={handleSelectColumn}
          />
        ) : null}
        <EntityStatus id={stage} statuses={APPLICANT_STAGES} />
        {isLoading ? (
          <Skeleton className={styles.skeletonCount} />
        ) : count > 0 ? (
          <Counter count={count} variant={TABLE_VARIANT} />
        ) : (
          <span className={styles.zeroCount}>{count}</span>
        )}
      </div>
      <div className={styles.track}>
        {isLoading ? (
          APPLICANT_BOARD_SKELETON_CARDS.map(skeletonCard => (
            <div
              {...getQaSelector({
                value: QA_NAME_APPLICANT_BOARD_SKELETON_CARD,
              })}
              className={styles.skeletonCard}
              key={skeletonCard}
            >
              <Skeleton className={styles.skeletonTitle} />
              <Skeleton className={styles.skeletonMeta} />
            </div>
          ))
        ) : items.length > 0 ? (
          visibleItems.map(item => (
            <RentalPortalApplicantBoardCard
              getActions={getActions}
              hasHandoff={hasHandoff}
              isListingScoped={isListingScoped}
              isSelectable={getIsItemSelectable(item)}
              isSelected={selectedIds.includes(item.id)}
              item={item}
              key={item.id}
              selectNotAllowedTooltip={selectNotAllowedTooltip}
              onCallAction={onCallAction}
              onClick={onClickCard}
              onSelect={hasSelection ? onSelectCard : undefined}
            />
          ))
        ) : (
          <Text isSecondary className={styles.empty}>
            {t('RentalPortal.ListingBoardEmptyColumnKey')}
          </Text>
        )}
      </div>
      {hiddenCount > 0 ? (
        <Button.Text
          className={styles.moreButton}
          qaName={QA_NAME_APPLICANT_BOARD_MORE}
          text={t('RentalPortal.ListingBoardMoreCountKey', {
            count: hiddenCount,
          })}
          onClick={handleClickMore}
        />
      ) : null}
    </div>
  )
}

export default RentalPortalApplicantBoardColumn
